import React, { useState, useEffect } from 'react';
import TopSearchBar from './JobSearch/TopSearchBar';
import JobListingCard from './JobSearch/JobListingCard';
import JobListingCardSkeleton from './JobSearch/JobListingCardSkeleton';
import JobDetailPanel from './JobSearch/JobDetailPanel';
import MobileSearchModal from './JobSearch/MobileSearchModal';
import ClientOnly from './ClientOnly';
import * as styles from './TaxonomyJobList.module.scss';

interface JobListing {
  id: number;
  title: string;
  max_reward: number | null;
  min_reward: number | null;
  location_name: string;
  created_at: string;
  is_new: boolean;
  contract_type: string;
  workstyle_type: number;
  workstyle_type_label: string | null;
  prefecture: { id: number; name: string } | null;
  city: string | null;
  station: string | null;
  published_at: string;
  skills: Array<{ id: number; name: string }>;
  occupations: Array<{ id: number; name: string }>;
  characteristics: Array<{ id: number; name: string }>;
  industries?: Array<{ id: number; name: string }>;
  availability_type?: number;
  status?: string;
  job_source: {
    id: number;
    name: string;
    display_name?: string;
    corporate_name?: string;
    logo_image: string;
    thumbnail_image: string;
  };
  is_favorited: boolean;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  from: number;
  to: number;
  per_page: number;
}

interface TaxonomyJobListProps {
  // 初期データ（SSR用）
  initialJobListings: JobListing[];
  initialPagination: Pagination;
  
  // Taxonomyコンテキスト
  taxonomyType: 'skill' | 'occupation' | 'industry' | 'job_source';
  taxonomyId: number;
  taxonomyName: string;
  
  // 検索フィルターの初期値
  initialKeyword?: string;
  initialPrefectureIds?: number[];
  initialPriceOver?: number;
  initialIsRecruiting?: boolean;
  initialRemoteTypes?: string[];
  initialWorkdayTypes?: string[];
  initialCharacteristicIds?: number[];
  
  // SEOコンテンツ(1ページ目のみ)
  seoContent?: {
    pageHeader: {
      title: string;
      totalCount: number;
      from: number;
      to: number;
      lastUpdated: string;
      hasSearchConditions: boolean;
    };
    comparisonTables?: string;
    charts: {
      salaryHistogram?: string;
      jobSourceHistogram?: string;
      occupationAverage?: string;
      industryAverage?: string;
      workstyleBreakdown?: string;
      jobSourceRewardAverage?: string;
    };
    description?: string;
    similarSkillsAgents?: string;
    similarOccupationsAgents?: string;
  } | null;
  
  // 現在のソート・ページ
  currentSort: string;
  currentPage: number;
  
  // ユーザー情報
  currentUserId?: number;
  isUserSignedIn: boolean;
  isProfileComplete: boolean;
  
  // 求人総数
  totalJobListingsCount?: number;
  
  // SSR フラグ
  isServerRendered?: boolean;
}

// このコンポーネントは、TaxonomyJobListコンポーネントのメインコンテナです。
const TaxonomyJobList: React.FC<TaxonomyJobListProps> = ({
  initialJobListings,
  initialPagination,
  taxonomyType,
  taxonomyId,
  taxonomyName,
  seoContent,
  currentSort,
  currentPage,
  currentUserId,
  isUserSignedIn,
  isProfileComplete,
  totalJobListingsCount,
  isServerRendered = false,
  initialKeyword = '',
  initialPrefectureIds = [],
  initialPriceOver,
  initialIsRecruiting = false,
  initialRemoteTypes = [],
  initialWorkdayTypes = [],
  initialCharacteristicIds = []
}) => {
  const [jobListings, setJobListings] = useState<JobListing[]>(initialJobListings);
  const [pagination, setPagination] = useState<Pagination>(initialPagination);
  const [sort, setSort] = useState<string>(currentSort);
  const [page, setPage] = useState<number>(currentPage);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMobileSearchModal, setShowMobileSearchModal] = useState(false);

  // SSRとクライアント側での条件評価結果を確認
  const isServer = typeof window === 'undefined';
  const shouldRenderHeader = seoContent && currentPage === 1;

  // SEOコンテンツがレンダリングされた後にStimulusコントローラーを初期化
  useEffect(() => {
    if (!seoContent || currentPage !== 1) return;

    // dangerouslySetInnerHTMLで挿入されたHTML要素にStimulusコントローラーを接続
    const initializeStimulusControllers = () => {
      // window.Stimulusが利用可能か確認
      if (typeof window !== 'undefined' && (window as any).Stimulus) {
        const stimulus = (window as any).Stimulus;
        
        // コンポーネント内のchart要素を探す
        const chartElements = document.querySelectorAll('[data-controller="chart"]');
        chartElements.forEach((element) => {
          // 既に接続されている場合はスキップ
          if (!element.getAttribute('data-stimulus-connected')) {
            try {
              // Stimulusコントローラーを手動で接続
              stimulus.load(element as HTMLElement);
              element.setAttribute('data-stimulus-connected', 'true');
            } catch (error) {
              console.warn('Failed to initialize Stimulus controller:', error);
            }
          }
        });
      }
    };

    // 少し遅延させてから初期化（DOMの更新を待つ）
    const timer = setTimeout(initializeStimulusControllers, 100);
    return () => clearTimeout(timer);
  }, [seoContent, currentPage]);

  // ソート・ページ変更時にAPIリクエスト
  useEffect(() => {
    fetchJobListings();
  }, [sort, page, initialCharacteristicIds, initialRemoteTypes]);

  const fetchJobListings = async () => {
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('sort', sort);
      searchParams.append('page', page.toString());
      
      // characteristic_ids を追加
      if (initialCharacteristicIds && initialCharacteristicIds.length > 0) {
        initialCharacteristicIds.forEach(id => {
          searchParams.append('characteristic_ids[]', id.toString());
        });
      }
      
      // リモートフィルターが有効な場合は追加
      if (initialRemoteTypes && initialRemoteTypes.length > 0 && (initialRemoteTypes.includes('remote') || initialRemoteTypes.includes('hybrid'))) {
        searchParams.append('remote', 'true');
      }
      
      // job_sourceの場合はfreelance_agentsを使用
      const taxonomyPath = taxonomyType === 'job_source' ? 'freelance_agents' : `${taxonomyType}s`;
      const url = `/job_listings/${taxonomyPath}/${taxonomyId}/jobs?${searchParams.toString()}`;
      const response = await fetch(url, { 
        headers: { 'Accept': 'application/json' } 
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setJobListings(data.job_listings);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch job listings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle job selection with scroll to top functionality for right panel
   * @param jobId - The job ID to select
   */
  const handleJobSelect = async (jobId: number) => {
    setSelectedJobId(jobId);
    setIsLoading(true);
    
    try {
      const response = await fetch(`/api/v1/job_listings/${jobId}`, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const jobDetail = await response.json();
      setSelectedJobDetail(jobDetail);
      
      // SSR時はスキップ
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      
      // Scroll right panel to top on PC view (768px and above)
      if (window.innerWidth >= 768) {
        // 求人詳細の読み込みが完了するまで少し待ってからスクロール処理を実行
        setTimeout(() => {
          // ERBテンプレートの要素を確実に発見するためのヘルパー関数
          const findERBElement = (selector: string, maxRetries = 10, delay = 50) => {
            return new Promise<Element | null>((resolve) => {
              let retries = 0;
              const checkElement = () => {
                const element = document.querySelector(selector);
                if (element) {
                  resolve(element);
                } else if (retries < maxRetries) {
                  retries++;
                  setTimeout(checkElement, delay);
                } else {
                  resolve(null);
                }
              };
              checkElement();
            });
          };

          // 右カラム（求人詳細）のスクロール
          findERBElement('.col-md-8.col-lg-8.d-none.d-md-block').then((rightColumn) => {
            if (rightColumn) {
              rightColumn.scrollTo({ top: 0, behavior: 'smooth' });
            }
          });
        }, 300); // 求人詳細の読み込み完了を待つため少し長めに設定
      }
    } catch (error) {
      console.error('Failed to fetch job detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobClose = () => {
    setSelectedJobId(null);
    setSelectedJobDetail(null);
  };

  const handleSortChange = (newSort: string) => {
    setSort(newSort);
    setPage(1); // ソート変更時は1ページ目に戻る
  };

  /**
   * Handle page change with scroll to top functionality for PC view
   * @param newPage - The page number to navigate to
   */
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    
    // SSR時はスキップ
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    // Scroll to top on PC view (768px and above)
    if (window.innerWidth >= 768) {
      // ページの更新が完了するまで少し待ってからスクロール処理を実行
      setTimeout(() => {
        // ERBテンプレートの要素を確実に発見するためのヘルパー関数
        const findERBElement = (selector: string, maxRetries = 10, delay = 50) => {
          return new Promise<Element | null>((resolve) => {
            let retries = 0;
            const checkElement = () => {
              const element = document.querySelector(selector);
              if (element) {
                resolve(element);
              } else if (retries < maxRetries) {
                retries++;
                setTimeout(checkElement, delay);
              } else {
                resolve(null);
              }
            };
            checkElement();
          });
        };

        // 1. 左サイドバーのスクロール
        findERBElement('[data-job-listings="true"]').then((scrollableParent) => {
          if (scrollableParent) {
            scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
        
        // 2. 右カラム（求人詳細）のスクロール
        findERBElement('.col-md-8.col-lg-8.d-none.d-md-block').then((rightColumn) => {
          if (rightColumn) {
            rightColumn.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
        
        // 3. ページ全体のスクロール - より確実な方法
        window.scrollTo({ top: 0, behavior: 'smooth' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 200); // 遅延を少し長くして確実性を向上
    }
  };

  return (
    <div>
      {/* トップ検索バー - ClientOnlyでラップしてhydration errorを回避 */}
      <ClientOnly>
        <TopSearchBar 
          keyword={initialKeyword}
          prefectureIds={initialPrefectureIds || []}
          priceOver={initialPriceOver}
          isRecruiting={initialIsRecruiting}
          remoteTypes={initialRemoteTypes || []}
          skillIds={taxonomyType === 'skill' ? [taxonomyId] : []}
          occupationIds={taxonomyType === 'occupation' ? [taxonomyId] : []}
          industryIds={taxonomyType === 'industry' ? [taxonomyId] : []}
          workdayTypes={initialWorkdayTypes || []}
          characteristicIds={initialCharacteristicIds || []}
          jobSourceIds={taxonomyType === 'job_source' ? [taxonomyId] : []}
          skillNames={taxonomyType === 'skill' ? { [taxonomyId]: taxonomyName } : {}}
          occupationNames={taxonomyType === 'occupation' ? { [taxonomyId]: taxonomyName } : {}}
          industryNames={taxonomyType === 'industry' ? { [taxonomyId]: taxonomyName } : {}}
          jobSourceNames={taxonomyType === 'job_source' ? { [taxonomyId]: taxonomyName } : {}}
          onSearch={(filters) => {
          // SSR時はスキップ
          if (typeof window === 'undefined') return;
          
          // 現在のタクソノミー情報を引き継いで検索ページにリダイレクト
          const searchParams = new URLSearchParams();
          
          // 現在のタクソノミーを追加
          if (taxonomyType === 'job_source') {
            searchParams.append('job_source_ids[]', taxonomyId.toString());
          } else {
            searchParams.append(`${taxonomyType}_ids[]`, taxonomyId.toString());
          }
          
          // 追加のフィルターがあれば追加
          if (filters.keyword) searchParams.append('keyword', filters.keyword);
          if (filters.prefecture_ids?.length) {
            filters.prefecture_ids.forEach(id => searchParams.append('prefecture_ids[]', id.toString()));
          }
          if (filters.price_over) searchParams.append('price_over', filters.price_over.toString());
          if (filters.is_recruiting) searchParams.append('is_recruiting', 'true');
          if (filters.remote_types?.length) {
            filters.remote_types.forEach(type => searchParams.append('remote_types[]', type));
          }
          if (filters.workday_types?.length) {
            filters.workday_types.forEach(type => searchParams.append('workday_types[]', type));
          }
          if (filters.characteristic_ids?.length) {
            filters.characteristic_ids.forEach(id => searchParams.append('characteristic_ids[]', id.toString()));
          }
          
          // 他のタクソノミーも追加（重複を避ける）
          if (taxonomyType !== 'skill' && filters.skill_ids?.length) {
            filters.skill_ids.forEach(id => searchParams.append('skill_ids[]', id.toString()));
          }
          if (taxonomyType !== 'occupation' && filters.occupation_ids?.length) {
            filters.occupation_ids.forEach(id => searchParams.append('occupation_ids[]', id.toString()));
          }
          if (taxonomyType !== 'industry' && filters.industry_ids?.length) {
            filters.industry_ids.forEach(id => searchParams.append('industry_ids[]', id.toString()));
          }
          // job_sourceの場合、filtersからも追加（他のタクソノミーページから検索する場合に備えて）
          if (taxonomyType !== 'job_source' && filters.job_source_ids?.length) {
            filters.job_source_ids.forEach(id => searchParams.append('job_source_ids[]', id.toString()));
          }
          // job_sourceの場合、filtersから追加のjob_source_idsがあれば追加（重複チェック済み）
          if (taxonomyType === 'job_source' && filters.job_source_ids?.length) {
            filters.job_source_ids.forEach(id => {
              if (id !== taxonomyId) {
                searchParams.append('job_source_ids[]', id.toString());
              }
            });
          }
          
          const searchUrl = `/job_listings/search?${searchParams.toString()}`;
          window.location.href = searchUrl;
        }}
        />
      </ClientOnly>
      
      <div className="container-fluid px-0 px-md-3">
        <div className="row g-0 mt-0">
          {/* 左カラム: 求人一覧 */}
          <div className="col-12 col-md-4 d-flex flex-column" data-job-listings="true">
            {/* ページヘッダー(1ページ目のみ) */}
            {seoContent && currentPage === 1 && (
              <div className="px-4">
                <h1 className="h3 fw-bold mt-4 mb-2 mb-md-2">{seoContent.pageHeader.title}</h1>
                <p className="text-muted mb-0 mb-md-3">
                  {pagination.total_count > 0
                    ? `${pagination.from}〜${pagination.to}件目 / 全${pagination.total_count}件`
                    : `全${pagination.total_count}件`}
                </p>
              </div>
            )}
            {/* 検索条件表示(パラメータがある場合のみ) */}
            {seoContent?.pageHeader.hasSearchConditions && (
              <div className="bg-white rounded shadow-sm p-3 mb-4 border-start border-primary border-4">
                {/* TODO: 検索条件の表示ロジックを実装 */}
              </div>
            )}
            {/* 求人一覧 */}
            <div className="bg-md-white rounded shadow-sm p-4 mb-4 flex-grow-1">
              {/* ヘッダー: タイトル、ソート、検索ボタン */}
              <div className="d-flex justify-content-end justify-content-md-between align-items-center mb-3">
                <h2 className="h5 fw-bold mb-0 d-none d-md-block">求人一覧</h2>
                <div className="d-flex gap-2">
                  {/* ソートドロップダウン */}
                  {jobListings.length > 0 && (
                    <select 
                      value={sort} 
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="form-select form-select-sm"
                      style={{ width: 'auto' }}
                    >
                      <option value="created_at desc">最新順</option>
                      <option value="max_reward desc">高単価順</option>
                    </select>
                  )}
                  
                  {/* 詳細検索ボタン */}
                  <a 
                    href={`/job_listings/search?${taxonomyType === 'job_source' ? 'job_source_ids[]' : `${taxonomyType}_ids[]`}=${taxonomyId}`}
                    className="btn btn-primary btn-sm d-none d-md-inline-block"
                  >
                    <i className="bi bi-sliders me-1"></i>条件を変更して検索
                  </a>
                </div>
              </div>
              
              {/* 件数表示 */}
              {pagination.total_count > 0 && (
                <div className="mb-3 d-none d-md-block">
                  <p className="text-muted small mb-0">
                    {pagination.from}〜{pagination.to}件目 / 全{pagination.total_count}件
                  </p>
                </div>
              )}
              
              {/* 求人一覧 or 空状態 */}
              {isLoading ? (
                <div className="row">
                  {Array.from({ length: Math.max(jobListings.length, 5) }).map((_, index) => (
                    <div key={`skeleton-${index}`} className="col-12 mb-3">
                      <JobListingCardSkeleton />
                    </div>
                  ))}
                </div>
              ) : jobListings.length > 0 ? (
                <>
                  <div className="row">
                    {jobListings.map(job => (
                      <div key={job.id} className="col-12 mb-3">
                        <JobListingCard 
                          jobListing={{
                            ...job,
                            prefecture: job.prefecture?.name || null
                          }}
                          onClick={() => handleJobSelect(job.id)}
                          isSelected={selectedJobId === job.id}
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* ページネーション */}
                  {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav aria-label="求人一覧のページネーション">
                        <ul className="pagination pagination-sm">
                          {/* 前のページ */}
                          {pagination.current_page > 1 && (
                            <li className="page-item">
                              <a 
                                href={`/job_listings/${taxonomyType}s/${taxonomyId}?page=${pagination.current_page - 1}${sort !== 'created_at desc' ? `&sort=${encodeURIComponent(sort)}` : ''}`}
                                className="page-link"
                                onClick={(e) => { e.preventDefault(); handlePageChange(pagination.current_page - 1); }}
                              >
                                前へ
                              </a>
                            </li>
                          )}
                          
                          {/* ページ番号の動的生成 */}
                          {(() => {
                            const totalPages = pagination.total_pages;
                            const currentPage = pagination.current_page;
                            const maxVisible = 5;
                            const pages = [];
                            
                            let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                            let end = Math.min(totalPages, start + maxVisible - 1);
                            
                            if (end - start + 1 < maxVisible) {
                              start = Math.max(1, end - maxVisible + 1);
                            }

                            // 最初のページと省略記号
                            if (start > 1) {
                              pages.push(
                                <li key={1} className="page-item">
                                  <a 
                                    href={`/job_listings/${taxonomyType}s/${taxonomyId}?page=1${sort !== 'created_at desc' ? `&sort=${encodeURIComponent(sort)}` : ''}`}
                                    className="page-link"
                                    onClick={(e) => { e.preventDefault(); handlePageChange(1); }}
                                  >
                                    1
                                  </a>
                                </li>
                              );
                              if (start > 2) {
                                pages.push(
                                  <li key="ellipsis-start" className="page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                            }

                            // ページ番号
                            for (let i = start; i <= end; i++) {
                              pages.push(
                                <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
                                  <a 
                                    href={`/job_listings/${taxonomyType}s/${taxonomyId}?page=${i}${sort !== 'created_at desc' ? `&sort=${encodeURIComponent(sort)}` : ''}`}
                                    className="page-link"
                                    onClick={(e) => { e.preventDefault(); handlePageChange(i); }}
                                  >
                                    {i}
                                  </a>
                                </li>
                              );
                            }

                            // 最後のページと省略記号
                            if (end < totalPages) {
                              if (end < totalPages - 1) {
                                pages.push(
                                  <li key="ellipsis-end" className="page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              pages.push(
                                <li key={totalPages} className="page-item">
                                  <a 
                                    href={`/job_listings/${taxonomyType}s/${taxonomyId}?page=${totalPages}${sort !== 'created_at desc' ? `&sort=${encodeURIComponent(sort)}` : ''}`}
                                    className="page-link"
                                    onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}
                                  >
                                    {totalPages}
                                  </a>
                                </li>
                              );
                            }

                            return pages;
                          })()}
                          
                          {/* 次のページ */}
                          {pagination.current_page < pagination.total_pages && (
                            <li className="page-item">
                              <a 
                                href={`/job_listings/${taxonomyType}s/${taxonomyId}?page=${pagination.current_page + 1}${sort !== 'created_at desc' ? `&sort=${encodeURIComponent(sort)}` : ''}`}
                                className="page-link"
                                onClick={(e) => { e.preventDefault(); handlePageChange(pagination.current_page + 1); }}
                              >
                                次へ
                              </a>
                            </li>
                          )}
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <i className="bi bi-search fs-1 text-muted mb-3 d-block"></i>
                  <h6 className="mb-2">該当する案件が見つかりませんでした</h6>
                  <a 
                    href={`/job_listings/search?${taxonomyType === 'job_source' ? 'job_source_ids[]' : `${taxonomyType}_ids[]`}=${taxonomyId}`}
                    className="btn btn-primary mt-2"
                  >
                    条件を変更して検索
                  </a>
                </div>
              )}
            </div>
          </div>
          
          {/* 右カラム: 求人詳細 + SEOコンテンツ */}
          <div className="col-md-8 col-lg-8 d-none d-md-block">
            <ClientOnly>
              <JobDetailPanel
                jobDetail={selectedJobDetail}
                onClose={handleJobClose}
                currentUserId={currentUserId}
                isUserSignedIn={isUserSignedIn}
                isProfileComplete={isProfileComplete}
                isLoading={isLoading}
                totalJobListingsCount={totalJobListingsCount}
              />
            </ClientOnly>
            
            {/* SEOコンテンツ(1ページ目のみ) - hydration error対策のため完全に削除 */}
            
            {/* 2ページ目以降: 1ページ目への誘導 */}
            {!seoContent && page > 1 && (
              <div className="card d-flex flex-column">
                <div className="card-body d-flex align-items-center justify-content-center" style={{ maxWidth: '800px', margin: '0 auto' }}>
                  <div className="text-center blog-content">
                    <i className="bi bi-bar-chart fa-3x mb-3 text-primary"></i>
                    <h5 className="fw-bold mb-3">
                      {taxonomyName}の詳細統計情報
                    </h5>
                    <p className="mb-3">
                      {taxonomyName}の月額単価相場、エージェントランキング、職種別・業界別平均単価などの詳細な統計情報は1ページ目でご確認いただけます。
                    </p>
                    <a href={taxonomyType === 'job_source' ? `/job_listings/freelance_agents/${taxonomyId}` : `/job_listings/${taxonomyType}s/${taxonomyId}`} className="btn btn-primary">
                      <i className="bi bi-arrow-left me-1"></i>1ページ目で統計情報を見る
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* スマホ用検索条件モーダル */}
      <ClientOnly>
        <MobileSearchModal
          show={showMobileSearchModal}
          onClose={() => setShowMobileSearchModal(false)}
          onSearch={(searchFilters) => {
            // 現在のタクソノミー情報を引き継いで検索ページにリダイレクト
            const searchParams = new URLSearchParams();
            
            // 現在のタクソノミーを追加
            if (taxonomyType === 'job_source') {
              searchParams.append('job_source_ids[]', taxonomyId.toString());
            } else {
              searchParams.append(`${taxonomyType}_ids[]`, taxonomyId.toString());
            }
            
            // 検索フィルターを追加
            if (searchFilters.keyword) searchParams.append('keyword', searchFilters.keyword);
            searchFilters.prefecture_ids.forEach(id => searchParams.append('prefecture_ids[]', id.toString()));
            if (searchFilters.price_over) searchParams.append('price_over', searchFilters.price_over.toString());
            if (searchFilters.is_recruiting) searchParams.append('is_recruiting', 'true');
            searchFilters.remote_types.forEach(type => searchParams.append('remote_types[]', type));
            searchFilters.workday_types.forEach(type => searchParams.append('workday_types[]', type));
            searchFilters.characteristic_ids.forEach(id => searchParams.append('characteristic_ids[]', id.toString()));
            
            // 他のタクソノミーも追加（重複を避ける）
            if (taxonomyType !== 'skill' && searchFilters.skill_ids.length > 0) {
              searchFilters.skill_ids.forEach(id => searchParams.append('skill_ids[]', id.toString()));
            }
            if (taxonomyType !== 'occupation' && searchFilters.occupation_ids.length > 0) {
              searchFilters.occupation_ids.forEach(id => searchParams.append('occupation_ids[]', id.toString()));
            }
            if (taxonomyType !== 'industry' && searchFilters.industry_ids.length > 0) {
              searchFilters.industry_ids.forEach(id => searchParams.append('industry_ids[]', id.toString()));
            }
            if (taxonomyType !== 'job_source' && searchFilters.job_source_ids && searchFilters.job_source_ids.length > 0) {
              searchFilters.job_source_ids.forEach(id => searchParams.append('job_source_ids[]', id.toString()));
            }
            if (taxonomyType === 'job_source' && searchFilters.job_source_ids && searchFilters.job_source_ids.length > 0) {
              searchFilters.job_source_ids.forEach(id => {
                if (id !== taxonomyId) {
                  searchParams.append('job_source_ids[]', id.toString());
                }
              });
            }
            
            const searchUrl = `/job_listings/search?${searchParams.toString()}`;
            window.location.href = searchUrl;
          }}
          initialFilters={{
            keyword: initialKeyword || '',
            prefecture_ids: initialPrefectureIds || [],
            price_over: initialPriceOver,
            is_recruiting: initialIsRecruiting || false,
            remote_types: initialRemoteTypes || [],
            skill_ids: taxonomyType === 'skill' ? [taxonomyId] : [],
            occupation_ids: taxonomyType === 'occupation' ? [taxonomyId] : [],
            industry_ids: taxonomyType === 'industry' ? [taxonomyId] : [],
            workday_types: initialWorkdayTypes || [],
            characteristic_ids: initialCharacteristicIds || [],
            job_source_ids: taxonomyType === 'job_source' ? [taxonomyId] : []
          }}
          skillNames={taxonomyType === 'skill' ? { [taxonomyId]: taxonomyName } : {}}
          occupationNames={taxonomyType === 'occupation' ? { [taxonomyId]: taxonomyName } : {}}
          industryNames={taxonomyType === 'industry' ? { [taxonomyId]: taxonomyName } : {}}
          jobSourceNames={taxonomyType === 'job_source' ? { [taxonomyId]: taxonomyName } : {}}
        />
      </ClientOnly>

    </div>
  );
};

export default TaxonomyJobList;
